/**
 * KullaniciBasligi.jsx — Kullanıcı Kartı'nın künye başlığı (backlog #379).
 *
 * Kişi Detay'ın künye başlığıyla (Detay/KunyeBasligi) aynı dil: solda vesikalık (bağlı kişinin fotoğrafı, yoksa yer
 * tutucu), ad soyad + rozetler (aktiflik, taraf, Yerli / Yabancı), kullanıcı adı / no / kimlik no / ünvan / telefon / e-posta,
 * firmalar, rol ve son giriş; sağda yazma düğmeleri.
 * Rozet olmayan her şey sekmelerden ulaşılır; başlıkta yalnızca kim olduğunu söyleyen bilgi durur.
 *
 * Yazma düğmeleri (#545) yalnızca MANAGE_USER_ROLES sahibine çizilir (`yazabilir`, sayfa belirler; backend de denetler):
 *   · Düzenle — kullanıcı adı, yeni şifre, ad, soyad, ünvan, telefon, e-posta, uyruk, durum (UserFormModal)
 *   · Şifre bağlantısı oluştur (#556) — `POST /users/{id}/sifre-linki`; kökün hesabında da çizilir, başkası isterse backend 403 döner ve
 *     sayfa o metni gösterir.
 *   · Aktivasyon maili gönder (#572) — `POST /users/{id}/aktivasyon-maili` (backend #568): yeni bir şifre belirleme bağlantısı kullanıcının kendi e-postasına
 *     gider (sayfa önce onay ister: yeni bağlantı öncekini iptal eder). E-posta adresi yoksa düğme kapalıdır ve nedenini söyler. Başlıkta mailin en son
 *     ne zaman gittiği de görünür.
 *   · MFA sıfırla (#611, backend #639) — `POST /mfa/reset/{userId}`: telefonunu kaybeden kullanıcının ikinci faktörünü kapatır (sayfa önce onay ister).
 *     Kullanıcının MFA durumu karta gelmediği için düğme hep çizilir; açık değilse sıfırlama kilitleri kaldırır, başka bir şey yapmaz.
 *   · Pasifleştir / Aktifleştir — `userActive` 0 / 1. Silme yoktur: backend silerken rol / fuar / firma bağlarını da kalıcı siler.
 *     Kök kullanıcıda düğme yok (kökün hesabı pasifleştirilemez, backend 409); kendini pasifleştirme kapalı çizilir ve nedenini söyler.
 */
import React from "react";
import { Button, Spinner } from "reactstrap";
import { Building2, Clock, IdCard, KeyRound, Mail, Pencil, Phone, Send, ShieldOff, UserCheck, UserCog, UserX } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  AktifRozeti, KimlikNo, KullaniciAvatar, UyrukRozeti, adSoyad, aktifMi, tarafAdi, tarafMeta, tarihSaat,
} from "../../../../Components/Kullanici/KullaniciParcalari";

export default function KullaniciBasligi({
  kullanici: u, yazabilir = false, kendisi = false, onDuzenle, onDurum, onSifreLinki, sifreLinkiIsleniyor = false,
  onAktivasyonMaili, aktivasyonMailiIsleniyor = false, onMfaSifirla, mfaSifirlaIsleniyor = false,
}) {
  const { t } = useTranslation("yonetim");
  const taraf = tarafMeta(u);
  const firmalar = u.firmalar || [];
  const aktif = aktifMi(u);
  const durumEngeli = aktif && kendisi ? t("kullaniciBasligi.kendiniPasiflestiremezsiniz") : null;

  return (
    <div className="d-flex align-items-center gap-3 border rounded-3 p-3 mb-3 bg-white flex-wrap">
      <KullaniciAvatar u={u} size={80} />

      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="fw-bold fs-5 text-break lh-sm">{adSoyad(u)}</span>
          <AktifRozeti u={u} />
          <span className="badge text-white fw-normal" style={{ background: taraf.hex }}>{tarafAdi(taraf)}</span>
          <UyrukRozeti u={u} />
        </div>

        <div className="d-flex align-items-center flex-wrap gap-3 small mt-1">
          <span className="font-monospace">{u.userLoginName}</span>
          <span className="text-muted font-monospace">#{u.userId}</span>
          {(u.kisi?.kimlikNo || u.kisi?.pasaportNo) && (
            <span className="d-inline-flex align-items-center gap-3 text-muted">
              <IdCard size={13} className="flex-shrink-0" /><KimlikNo u={u} />
            </span>
          )}
          {u.userTitle && <span className="text-muted">{u.userTitle}</span>}
          {u.userPhone && (
            <span className="d-inline-flex align-items-center gap-1 text-muted"><Phone size={12} />{u.userPhone}</span>
          )}
          {u.userEmail && (
            <span className="d-inline-flex align-items-center gap-1 text-muted text-break"><Mail size={12} />{u.userEmail}</span>
          )}
        </div>

        <div className="d-flex align-items-center flex-wrap gap-3 small mt-1">
          {u.userGroupName && (
            <span className="d-inline-flex align-items-center gap-1"><UserCog size={13} className="text-muted" />{u.userGroupName}</span>
          )}
          {firmalar.length > 0
            ? firmalar.map((f) => (
              <span
                key={f.firmaId} className="d-inline-flex align-items-center gap-1 text-break"
                title={(f.fuarRolAdlari || []).join(", ") || undefined}
              >
                <Building2 size={13} className="text-muted flex-shrink-0" />{f.firmaName || t("kullaniciBasligi.firmaNo", { id: f.firmaId })}
              </span>
            ))
            : <span className="text-muted d-inline-flex align-items-center gap-1"><Building2 size={13} />{t("kullaniciBasligi.firmaYok")}</span>}
          <span className="d-inline-flex align-items-center gap-1 text-muted" title={t("kullaniciBasligi.buSistemdekiSonGiris")}>
            <Clock size={13} />{u.userLastLogin ? tarihSaat(u.userLastLogin) : t("kullaniciBasligi.hicGirisYapmadi")}
          </span>
          {(u.aktivasyonMailiTarihi || u.aktivasyonMailiGonderildi) && (
            <span className="d-inline-flex align-items-center gap-1 text-muted" title={t("kullaniciBasligi.aktivasyonMailiBaslik")}>
              <Send size={13} />
              {u.aktivasyonMailiTarihi ? t("kullaniciBasligi.aktivasyonMailiTarihi", { tarih: tarihSaat(u.aktivasyonMailiTarihi) }) : t("kullaniciBasligi.aktivasyonMailiGitti")}
            </span>
          )}
        </div>
      </div>

      {yazabilir && (
        <div className="d-flex gap-2 flex-shrink-0 align-self-start flex-wrap">
          {onDuzenle && (
            <Button color="light" className="border" size="sm" onClick={onDuzenle}>
              <Pencil size={13} className="me-1" />{t("kullaniciBasligi.duzenle")}
            </Button>
          )}
          {onSifreLinki && (
            <Button color="light" className="border" size="sm" disabled={sifreLinkiIsleniyor} onClick={onSifreLinki}>
              {sifreLinkiIsleniyor ? <Spinner size="sm" className="me-1" /> : <KeyRound size={13} className="me-1" />}
              {t("kullaniciBasligi.sifreLinkiOlustur")}
            </Button>
          )}
          {onAktivasyonMaili && (
            <span title={u.userEmail ? undefined : t("kullaniciBasligi.epostaYok")}>
              <Button color="light" className="border" size="sm" disabled={aktivasyonMailiIsleniyor || !u.userEmail} onClick={onAktivasyonMaili}>
                {aktivasyonMailiIsleniyor ? <Spinner size="sm" className="me-1" /> : <Send size={13} className="me-1" />}
                {t("kullaniciBasligi.aktivasyonMailiGonder")}
              </Button>
            </span>
          )}
          {onMfaSifirla && (
            <Button color="light" className="border" size="sm" disabled={mfaSifirlaIsleniyor} onClick={onMfaSifirla}>
              {mfaSifirlaIsleniyor ? <Spinner size="sm" className="me-1" /> : <ShieldOff size={13} className="me-1" />}
              {t("kullaniciBasligi.mfaSifirla")}
            </Button>
          )}
          {onDurum && !u.root && (
            <span title={durumEngeli || undefined}>
              <Button color="light" className="border" size="sm" disabled={Boolean(durumEngeli)} onClick={onDurum}>
                {aktif
                  ? <><UserX size={13} className="me-1" />{t("kullaniciBasligi.pasiflestir")}</>
                  : <><UserCheck size={13} className="me-1" />{t("kullaniciBasligi.aktiflestir")}</>}
              </Button>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
